import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Target,
  Award,
  Zap,
  BookOpen,
  CheckCircle,
  Loader2,
  AlertCircle,
  Star,
  ExternalLink,
  GraduationCap,
  MapPin,
  Clock,
  Calendar
} from 'lucide-react';

interface Suggestion {
  id: string;
  title: string;
  ectsCredits: number;
  eqfLevel: number;
  estimatedHours: number;
  organization: string;
  association: string;
  relevanceScore: number;
  matchingCompetencies?: string[];
  reason: string;
  completionPath?: string;
  certificatesInPath?: number;
}

interface LearningPathData {
  currentState: {
    totalCertificates: number;
    totalECTS: number;
    currentEQFLevel: number;
    eqfLevels: number[];
    competencyAreas: Array<{ name: string; certificateCount: number }>;
    strongestCompetencies: Array<{ name: string; count: number; level: number }>;
  };
  progressMetrics: {
    currentLevel: string;
    totalECTS: number;
    highestEQFLevel: number;
    nextMilestone: {
      label: string;
      ectsRequired: number;
      ectsRemaining: number;
      eqfRequired: number;
      progressPercentage: number;
    } | null;
    breadth: number;
    recommendation: string;
  };
  suggestions: {
    nextLevel: {
      targetEQFLevel: number;
      count: number;
      suggestions: Suggestion[];
    };
    relatedCompetencies: {
      basedOnCompetencies: string[];
      count: number;
      suggestions: Suggestion[];
    };
    completionPaths: {
      count: number;
      suggestions: Suggestion[];
    };
  };
}

interface ExternalCourse {
  id: string;
  title: string;
  description?: string;
  category?: string;
  level?: string;
  locationType?: string;
  location?: string;
  duration?: string;
  price?: string;
  provider?: string;
  studyAssociation?: string;
  url?: string;
  startDate?: string;
  endDate?: string;
  relevanceScore: number;
  matchReasons: string[];
  learningObjectives?: string;
  prerequisites?: string;
}

interface CoursesResponse {
  success: boolean;
  apiConfigured?: boolean;
  message?: string;
  courses: ExternalCourse[];
  hasMore?: boolean;
  matched?: number;
}

export function LearningPathPage() {
  const [learningPath, setLearningPath] = useState<LearningPathData | null>(null);
  const [externalCourses, setExternalCourses] = useState<ExternalCourse[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesApiConfigured, setCoursesApiConfigured] = useState(true);
  const [coursesMessage, setCoursesMessage] = useState<string | null>(null);
  const [hasMoreCourses, setHasMoreCourses] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLearningPath();
    fetchExternalCourses();
  }, []);

  async function fetchLearningPath() {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/learning-path/suggestions', {
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Kunne ikke hente læringssti-forslag');
      }

      const data = await res.json();

      if (!data.learningPath) {
        setError('Få din første attest for å motta personlige læringsforslag!');
        setLearningPath(null);
      } else {
        setLearningPath(data.learningPath);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchExternalCourses() {
    try {
      setCoursesLoading(true);
      const res = await fetch('/api/learning-path/course-suggestions?limit=6', {
        credentials: 'include'
      });

      if (!res.ok) {
        console.error('Failed to fetch external courses');
        return;
      }

      const data: CoursesResponse = await res.json();

      if (data.success) {
        setExternalCourses(data.courses || []);
        setCoursesApiConfigured(data.apiConfigured !== false);
        setCoursesMessage(data.message || null);
        setHasMoreCourses(data.hasMore === true);
      }
    } catch (err) {
      console.error('Error fetching external courses:', err);
    } finally {
      setCoursesLoading(false);
    }
  }

  async function loadMoreCourses() {
    try {
      setLoadingMore(true);
      const offset = externalCourses.length;
      const res = await fetch(`/api/learning-path/course-suggestions?limit=6&offset=${offset}`, {
        credentials: 'include'
      });

      if (!res.ok) {
        console.error('Failed to load more courses');
        return;
      }

      const data: CoursesResponse = await res.json();

      if (data.success && data.courses) {
        // Append new courses to existing list
        setExternalCourses(prev => [...prev, ...data.courses]);
        setHasMoreCourses(data.hasMore === true);
      }
    } catch (err) {
      console.error('Error loading more courses:', err);
    } finally {
      setLoadingMore(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="card text-center py-12">
          <Loader2 className="w-12 h-12 text-kf-orange-600 mx-auto mb-4 animate-spin" />
          <p className="text-kf-gray-600">Analyserer din læringsreise...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isEmpty = error.toLowerCase().includes('attest') || error.toLowerCase().includes('første');
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card text-center py-12">
          <AlertCircle className="w-16 h-16 text-kf-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-kf-gray-900 mb-2">
            {isEmpty ? 'Lommeboken er tom' : 'Ingen læringssti tilgjengelig'}
          </h2>
          <p className="text-kf-gray-600 mb-6 max-w-lg mx-auto">
            {isEmpty
              ? 'Læringssti gir personlige kursforslag basert på det du allerede kan. Vi trenger minst én kompetanseattest eller ett vitnemål for å analysere kompetansen din. Importer fra Vitnemålsportalen, eller meld deg på et kurs hos en av våre medlemsorganisasjoner.'
              : error}
          </p>
          {isEmpty && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/dashboard"
                className="bg-kf-blue-600 hover:bg-kf-blue-700 text-white font-medium px-5 py-2.5 rounded-lg inline-flex items-center justify-center gap-2"
              >
                Hent fra Vitnemålsportalen
              </Link>
              <Link
                to="/welcome"
                className="border border-kf-blue-600 text-kf-blue-600 hover:bg-kf-blue-50 font-medium px-5 py-2.5 rounded-lg inline-flex items-center justify-center gap-2"
              >
                Hva er en kompetanseattest?
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!learningPath) {
    return null;
  }

  const { currentState, progressMetrics, suggestions } = learningPath;

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-kf-gray-900">Læringssti</h1>
        <p className="text-kf-gray-600 mt-1">
          Personlige anbefalinger basert på dine attester og kompetanser
        </p>
      </div>

      {/* Progress Overview */}
      <div className="card bg-gradient-to-br from-kf-orange-500 to-kf-orange-700 text-white">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Din progresjon</h2>
            <p className="text-lg opacity-90">
              Nivå: <span className="font-bold">{progressMetrics.currentLevel}</span>
            </p>
          </div>
          <Award className="w-16 h-16 opacity-80" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white/20 rounded-lg p-4">
            <p className="text-sm opacity-90">Totale Kompetansepoeng (ECTS)</p>
            <p className="text-3xl font-bold">{progressMetrics.totalECTS}</p>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <p className="text-sm opacity-90">Høyeste EQF-nivå</p>
            <p className="text-3xl font-bold">{progressMetrics.highestEQFLevel}</p>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <p className="text-sm opacity-90">Attester</p>
            <p className="text-3xl font-bold">{currentState.totalCertificates}</p>
          </div>
        </div>

        {progressMetrics.nextMilestone && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold">
                Neste milepæl: {progressMetrics.nextMilestone.label}
              </p>
              <p className="text-sm">
                {progressMetrics.nextMilestone.ectsRemaining} ECTS igjen
              </p>
            </div>
            <div className="w-full bg-white/30 rounded-full h-3 overflow-hidden">
              <div
                className="bg-white h-full transition-all duration-500"
                style={{ width: `${progressMetrics.nextMilestone.progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-white/20 rounded-lg">
          <p className="text-sm opacity-90 mb-1">Anbefaling</p>
          <p className="font-medium">{progressMetrics.recommendation}</p>
        </div>
      </div>

      {/* External Course Suggestions */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-emerald-100 rounded-lg">
            <GraduationCap className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-kf-gray-900">Anbefalte kurs</h2>
            <p className="text-kf-gray-600">
              Kurs fra kompetanseheving.no som passer din profil
            </p>
          </div>
        </div>

        {coursesLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <span className="ml-3 text-kf-gray-600">Henter kursforslag...</span>
          </div>
        ) : externalCourses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {externalCourses.map((course) => (
              <div
                key={course.id}
                className="p-4 border-2 border-emerald-200 bg-emerald-50 rounded-lg hover:border-emerald-400 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-kf-gray-900 text-lg flex-1 pr-2">
                    {course.title}
                  </h3>
                  {course.url && (
                    <a
                      href={course.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                      title="Åpne kurs"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>

                {course.description && (
                  <p className="text-sm text-kf-gray-600 mb-3 line-clamp-2">
                    {course.description}
                  </p>
                )}

                <div className="flex flex-wrap gap-2 mb-3">
                  {course.category && (
                    <span className="px-2 py-1 bg-emerald-600 text-white text-xs font-medium rounded-full">
                      {course.category}
                    </span>
                  )}
                  {course.level && (
                    <span className="px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full">
                      {course.level}
                    </span>
                  )}
                  {course.locationType && (
                    <span className="px-2 py-1 bg-kf-gray-200 text-kf-gray-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {course.locationType}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 text-sm text-kf-gray-600 mb-3">
                  {course.duration && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {course.duration}
                    </span>
                  )}
                  {course.startDate && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(course.startDate).toLocaleDateString('nb-NO')}
                    </span>
                  )}
                  {course.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {course.location}
                    </span>
                  )}
                </div>
                {(course.provider || course.price) && (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-kf-gray-600">
                    {course.provider && (
                      <span className="font-medium">{course.provider}</span>
                    )}
                    {course.price && (
                      <span className="text-emerald-700">{course.price}</span>
                    )}
                  </div>
                )}

                {course.matchReasons && course.matchReasons.length > 0 && (
                  <div className="mt-2 p-2 bg-white rounded border border-emerald-200">
                    <p className="text-xs text-emerald-700 font-medium mb-1">Hvorfor dette kurset passer deg:</p>
                    <ul className="text-xs text-kf-gray-600 space-y-1">
                      {course.matchReasons.slice(0, 2).map((reason, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-kf-gray-500">
            <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
            {!coursesApiConfigured ? (
              <>
                <p className="font-medium text-kf-gray-700">Kurskatalogen er under oppkobling</p>
                <p className="text-sm mt-2">
                  {coursesMessage || 'Besøk kompetanseheving.no for å se tilgjengelige kurs.'}
                </p>
              </>
            ) : (
              <>
                <p>Ingen kursforslag tilgjengelig for øyeblikket.</p>
                <p className="text-sm mt-2">
                  Du kan selv søke i kurskatalogen ved å besøke{' '}
                  <a
                    href="https://www.kompetanseheving.no"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 underline"
                  >
                    www.kompetanseheving.no
                  </a>
                </p>
              </>
            )}
          </div>
        )}

        {/* Load More Button */}
        {hasMoreCourses && externalCourses.length > 0 && (
          <div className="mt-4 text-center">
            <button
              onClick={loadMoreCourses}
              disabled={loadingMore}
              className="px-6 py-2 bg-emerald-100 text-emerald-700 font-medium rounded-lg hover:bg-emerald-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Laster...
                </>
              ) : (
                <>
                  <BookOpen className="w-4 h-4" />
                  Se flere forslag
                </>
              )}
            </button>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-kf-gray-200 text-center">
          <a
            href="https://kompetanseheving.no"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center gap-2"
          >
            Se alle kurs på kompetanseheving.no
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Top Competencies */}
      {currentState.strongestCompetencies.length > 0 && (
        <div className="card">
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-4">⭐ Dine sterkeste kompetanser</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {currentState.strongestCompetencies.map((comp, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-gradient-to-r from-kf-orange-50 to-kf-orange-100 rounded-lg border border-kf-orange-200"
              >
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-kf-orange-600" />
                  <span className="font-semibold text-kf-gray-900">{comp.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-kf-gray-600">{comp.count}x</span>
                  <span className="px-2 py-1 bg-kf-orange-600 text-white text-xs font-semibold rounded-full">
                    EQF {comp.level}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next Level Suggestions */}
      {suggestions.nextLevel.suggestions.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-kf-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-kf-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-kf-gray-900">
                Neste nivå (EQF {suggestions.nextLevel.targetEQFLevel})
              </h2>
              <p className="text-kf-gray-600">
                Disse kursene bygger videre på din kompetanse
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {suggestions.nextLevel.suggestions.map((cert) => (
              <div
                key={cert.id}
                className="p-4 border-2 border-kf-blue-200 bg-kf-blue-50 rounded-lg hover:border-kf-blue-400 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-kf-gray-900 text-lg">{cert.title}</h3>
                    <p className="text-sm text-kf-gray-600">
                      {cert.organization} • {cert.association}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-kf-blue-600 text-white text-sm font-semibold rounded-full">
                      {cert.ectsCredits} ECTS
                    </span>
                    <span className="px-3 py-1 bg-kf-blue-600 text-white text-sm font-semibold rounded-full">
                      EQF {cert.eqfLevel}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-kf-gray-700 mb-2">
                  <Target className="w-4 h-4 text-kf-blue-600" />
                  <span className="italic">{cert.reason}</span>
                </div>

                {cert.matchingCompetencies && cert.matchingCompetencies.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <span className="text-xs text-kf-gray-600">Matcher:</span>
                    {cert.matchingCompetencies.slice(0, 3).map((comp, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-white text-kf-blue-700 text-xs font-medium rounded border border-kf-blue-300"
                      >
                        {comp}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related Competencies */}
      {suggestions.relatedCompetencies.suggestions.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-kf-orange-100 rounded-lg">
              <Zap className="w-6 h-6 text-kf-orange-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-kf-gray-900">Relaterte kompetanser</h2>
              <p className="text-kf-gray-600">
                Basert på: {suggestions.relatedCompetencies.basedOnCompetencies.join(', ')}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {suggestions.relatedCompetencies.suggestions.map((cert) => (
              <div
                key={cert.id}
                className="p-4 border-2 border-kf-orange-200 bg-kf-orange-50 rounded-lg hover:border-kf-orange-600 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-kf-gray-900 text-lg">{cert.title}</h3>
                    <p className="text-sm text-kf-gray-600">
                      {cert.organization} • {cert.association}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-kf-orange-600 text-white text-sm font-semibold rounded-full">
                      {cert.ectsCredits} ECTS
                    </span>
                    <span className="px-3 py-1 bg-kf-orange-600 text-white text-sm font-semibold rounded-full">
                      EQF {cert.eqfLevel}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-kf-gray-700">
                  <Target className="w-4 h-4 text-kf-orange-600" />
                  <span className="italic">{cert.reason}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completion Paths */}
      {suggestions.completionPaths.suggestions.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-kf-blue-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-kf-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-kf-gray-900">Fullføringsveier</h2>
              <p className="text-kf-gray-600">
                Disse kursene kompletterer dine eksisterende kompetanseområder
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {suggestions.completionPaths.suggestions.map((cert) => (
              <div
                key={cert.id}
                className="p-4 border-2 border-kf-blue-200 bg-kf-blue-50 rounded-lg hover:border-kf-blue-400 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-kf-gray-900 text-lg">{cert.title}</h3>
                    <p className="text-sm text-kf-gray-600">
                      {cert.organization} • {cert.association}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-kf-blue-600 text-white text-sm font-semibold rounded-full">
                      {cert.ectsCredits} ECTS
                    </span>
                    <span className="px-3 py-1 bg-kf-blue-600 text-white text-sm font-semibold rounded-full">
                      EQF {cert.eqfLevel}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-kf-gray-700 mb-2">
                  <CheckCircle className="w-4 h-4 text-kf-blue-600" />
                  <span className="italic">{cert.reason}</span>
                </div>

                <div className="mt-2 p-2 bg-white rounded border border-kf-blue-300">
                  <p className="text-xs text-kf-gray-600">
                    <strong>{cert.completionPath}</strong> - {cert.certificatesInPath} attest
                    {cert.certificatesInPath && cert.certificatesInPath > 1 ? 'er' : ''} fullført
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Suggestions - only show if no internal suggestions AND no external courses */}
      {suggestions.nextLevel.suggestions.length === 0 &&
        suggestions.relatedCompetencies.suggestions.length === 0 &&
        suggestions.completionPaths.suggestions.length === 0 &&
        externalCourses.length === 0 && (
          <div className="card text-center py-12">
            <AlertCircle className="w-16 h-16 text-kf-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-kf-gray-900 mb-2">
              Ingen forslag tilgjengelig for øyeblikket
            </h3>
            <p className="text-kf-gray-600">
              Vi kunne ikke finne passende attester eller kurs basert på dine nåværende kompetanser.
            </p>
            <p className="text-kf-gray-600 mt-2">
              Du kan selv søke i kurskatalogen ved å besøke{' '}
              <a
                href="https://www.kompetanseheving.no"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 underline"
              >
                www.kompetanseheving.no
              </a>
            </p>
          </div>
        )}
    </div>
  );
}
